/**
 * RoadmapFloatingButton — botón flotante abajo-derecha en `/agendar` que abre
 * el mapa de la ruta de terapia de pareja (`CoupleRoadmapMap`).
 *
 * Solo se monta cuando hay progreso real (`hasProgressData` en el padre) —
 * el "modo preview" original de la spec (mostrarlo también a paciente nuevo,
 * §7.6) fue descartado a pedido del director (2026-09-14): sin historial de
 * pareja, la trayectoria no aplica y el ícono es ruido. Por eso acá adentro
 * ya no hay rama "sin progreso" — si el componente está montado, siempre hay
 * sesión/total válidos.
 *
 * Spec: docs/spec_roadmap_pareja_map.md §1, §5. DOM, a11y y CSS de esta spec —
 * no re-derivados acá.
 *
 * Auto-hide en mobile: mismo patrón y umbral (`NEAR_BOTTOM_PX = 200`) que
 * `KonexGuide.tsx` (`data-nearbottom`), gatillado solo en los 3 pasos con
 * navegación apilada (service-therapist / calendar / patient-confirmation).
 * Se duplica acá en vez de extraer un hook compartido — decisión de `dev`
 * explícitamente dejada abierta por la spec (§11): no bloqueante, la lógica es
 * chica y extraerla tocaría KonexGuide.tsx, que está fuera del alcance de este
 * cambio.
 *
 * En el paso `calendar` además se oculta si tapa el widget del calendario
 * (`.calendar-widget`) — mismo mecanismo `data-nearbottom`, ver `measure()`.
 * Reportado 2026-09-14: el fab quedaba fijo sobre la grilla de días.
 */

import { forwardRef, useEffect, useState } from 'react';
import type { BookingStep } from '../BookingWizard';
import './RoadmapFloatingButton.css';

interface RoadmapFloatingButtonProps {
  currentStep: BookingStep;
  isOpen: boolean;
  sesionActual: number;
  total: number;
  onOpen: () => void;
}

const MOBILE_QUERY = '(max-width: 768px)';
/** Spec §5.2 — mismo umbral que KonexGuide (NEAR_BOTTOM_PX). */
const NEAR_BOTTOM_PX = 200;
/** Franja inferior derecha que ocupa el fab (tamaño + margen, ver .css mobile). */
const CALENDAR_OVERLAP_BAND_PX = 90;

function stepHasWizardHeader(step: BookingStep): boolean {
  return step === 'service-therapist' || step === 'calendar' || step === 'patient-confirmation';
}

function computeIsMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * `ref` forwardeado al `<button>` — spec §11: al cerrar el mapa (X, Esc, tap en
 * scrim) el foco vuelve acá. `CoupleRoadmapMap` recibe el mismo nodo vía
 * `returnFocusRef` (ref compartido creado en `BookingWizardContent`).
 */
const RoadmapFloatingButton = forwardRef<HTMLButtonElement, RoadmapFloatingButtonProps>(function RoadmapFloatingButton(
  { currentStep, isOpen, sesionActual, total, onOpen },
  ref
) {
  const [isMobile, setIsMobile] = useState<boolean>(computeIsMobile);
  const [nearBottom, setNearBottom] = useState(false);
  const hasWizardHeader = stepHasWizardHeader(currentStep);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !hasWizardHeader) {
      setNearBottom(false);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollable = scrollHeight > window.innerHeight + 1;
      const nearPageBottom =
        scrollable && window.innerHeight + window.scrollY >= scrollHeight - NEAR_BOTTOM_PX;

      let overlapsCalendar = false;
      if (currentStep === 'calendar') {
        const widget = document.querySelector('.calendar-widget');
        if (widget) {
          const rect = widget.getBoundingClientRect();
          overlapsCalendar =
            rect.bottom > window.innerHeight - CALENDAR_OVERLAP_BAND_PX && rect.top < window.innerHeight;
        }
      }

      setNearBottom(nearPageBottom || overlapsCalendar);
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    measure();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [isMobile, hasWizardHeader, currentStep]);

  // Spec §5.3 — el número real vive también en el aria-label (WCAG 1.4.1).
  // Siempre hay progreso acá: el padre solo monta este componente con
  // hasProgressData=true.
  const ariaLabel = `Ver la ruta de tu terapia de pareja — sesión ${Math.max(sesionActual, 1)} de ${total}`;

  return (
    <button
      ref={ref}
      type="button"
      className="roadmap-fab"
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls="roadmap-map-dialog"
      aria-label={ariaLabel}
      aria-hidden={isOpen || undefined}
      tabIndex={isOpen ? -1 : 0}
      data-nearbottom={String(nearBottom)}
      onClick={onOpen}
    >
      <svg className="roadmap-fab__icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24">
        <path
          d="M4 20c3-6 5-9 8-9s3 4 6 4 4-6 2-11"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </svg>

      <span className="roadmap-fab__badge" aria-hidden="true">
        {Math.max(sesionActual, 1)}/{total}
      </span>
    </button>
  );
});

export default RoadmapFloatingButton;
